import { Button, Typography, useTheme } from '@mui/material';
import { MdChevronLeft, MdChevronRight } from '@teamworksdev/icons';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCalendarContext } from '../../context/CalendarContext';

export function CalendarHeader() {
  const { t } = useTranslation();
  const {
    palette: { greyscaleLight, action },
  } = useTheme();
  const { currentDate, navigateToDate, minDate, maxDate } =
    useCalendarContext();

  const formattedDate = currentDate.toFormat('LLLL yyyy');

  const canGoToPreviousMonth = useMemo(() => {
    const previousMonth = currentDate.minus({ months: 1 }).startOf('month');
    return previousMonth >= minDate;
  }, [currentDate, minDate]);

  const canGoToNextMonth = useMemo(() => {
    const nextMonth = currentDate.plus({ months: 1 }).startOf('month');
    return nextMonth <= maxDate;
  }, [currentDate, maxDate]);

  const goToPreviousMonth = () => {
    if (!canGoToPreviousMonth) return;
    navigateToDate(currentDate.minus({ months: 1 }));
  };

  const goToNextMonth = () => {
    if (!canGoToNextMonth) return;
    navigateToDate(currentDate.plus({ months: 1 }));
  };

  const goToToday = () => {
    navigateToDate(DateTime.now());
  };

  return (
    <div className="flex items-center justify-between px-4 pt-4 pb-2 h-[48px] bg-white rounded-tl-xl rounded-tr-xl border-t border-l border-r border-gray-200">
      <Typography variant="h3">{formattedDate}</Typography>
      <div className="flex items-stretch gap-1 select-none">
        <Button
          size="small"
          onClick={goToPreviousMonth}
          disabled={!canGoToPreviousMonth}
          variant="outlined"
          className="flex items-center px-1"
          aria-label={t('previousMonth')}
          sx={{
            '&.Mui-disabled': {
              borderColor: action.disabledBackground,
              backgroundColor: greyscaleLight[50],
            },
          }}
        >
          <MdChevronLeft
            size={20}
            color={canGoToPreviousMonth ? 'inherit' : greyscaleLight[300]}
          />
        </Button>
        <Button
          size="small"
          onClick={goToToday}
          variant="outlined"
          className="flex items-center px-2"
        >
          {t('today')}
        </Button>
        <Button
          size="small"
          onClick={goToNextMonth}
          disabled={!canGoToNextMonth}
          variant="outlined"
          className="flex items-center px-1"
          aria-label={t('nextMonth')}
          sx={{
            '&.Mui-disabled': {
              borderColor: action.disabledBackground,
              backgroundColor: greyscaleLight[50],
            },
          }}
        >
          <MdChevronRight
            size={20}
            color={canGoToNextMonth ? 'inherit' : greyscaleLight[300]}
          />
        </Button>
      </div>
    </div>
  );
}
